<template>
<form
  :method="methodType"
  accept-charset="UTF-8"
  autocomplete="off"
  :action="actionUrl"
  id="purchasableItemForm"
  :reset="true"
  v-on:submit.prevent="submit"
>
  <div class="form-group">
    <label for="purchasable-item-name">
      Name
    </label>
    <input
      type="text"
      name="purchasable_item[name]"
      id="purchasable-item-name"
      v-model="name"
      class="form-control"
    />
  </div>

  <div class="form-group">
    <label for="purchasable-item-description">
      Description
    </label>
    <textarea
      name="purchasable_item[description]"
      id="purchasable-item-description"
      v-model="description"
      class="form-control"
    ></textarea>
  </div>

  <div class="form-group">
    <label for="purchasable-item-price">
      Price:
    </label>
    <div class="input-group">
      <div class="input-group-prepend">
        <span class="input-group-text">$</span>
      </div>
      <input
        type="number"
        name="purchasable_item[price]"
        id="purchasable-item-price"
        step=".01"
        v-model="price"
        class="form-control"
      />
    </div>
  </div>

  <div class="form-group">
    <label for="purchasable-item-udids">
      UDIDS
    </label>
    <textarea
      name="purchasable_item[udids]"
      id="purchasable-item-udids"
      v-model="udids"
      class="form-control"
    ></textarea>
  </div>

  <div class="form-group">
    <div class="row">
      <div class="col-2">
        <label
          for="purchasable-item-subscription"
          class="form-check-label col-form-label"
        >
          <input name="purchasable_item[subscription]" type="hidden" value="false" />
          <input
            type="checkbox"
            name="purchasable_item[subscription]"
            id="purchasable-item-subscription"
            class="form-check-input"
            v-model="subscription"
          />
          Subscription?
        </label>
      </div>
      <div class="col-2">
        <label
          for="purchasable-item-recurring"
          class="form-check-label col-form-label"
        >
          <input name="purchasable_item[recurring]" type="hidden" value="false" />
          <input
            type="checkbox"
            name="purchasable_item[recurring]"
            id="purchasable-item-recurring"
            class="form-check-input"
            v-model="recurring"
          />
          Recurring?
        </label>
      </div>
      <div class="col-2">
        <label
          for="purchasable-item-renewal"
          class="form-check-label col-form-label"
        >
          <input name="purchasable_item[renewal]" type="hidden" value="false" />
          <input
            type="checkbox"
            name="purchasable_item[renewal]"
            id="purchasable-item-renewal"
            class="form-check-input"
            v-model="renewal"
          />
          Renewal?
        </label>
      </div>
      <div class="col-2">
        <label
          for="purchasable-item-featured"
          class="form-check-label col-form-label"
        >
          <input name="purchasable_item[featured]" type="hidden" value="false" />
          <input
            type="checkbox"
            name="purchasable_item[featured]"
            id="purchasable-item-featured"
            class="form-check-input"
            v-model="featured"
          />
          Featured?
        </label>
      </div>
      <div class="col-4">
        <label for="purchasable-item-subscription">
          Subscription Interval in Weeks<br />
        </label>
        <input
          type="number"
          name="purchasable_item[subscription_interval]"
          id="purchasable-item-subscription_interval"
          placeholder="52"
          v-model="formattedSubscriptionIntv"
          class="form-control"
        />
      </div>
    </div>
  </div>

  <div class="form-group" v-if="recurring">
    <div class="alert alert-warning text-center" role="alert">
      <p>
        Recurring purchasable items must have a corresponding
        <a href="https://www.paypal.com/cgi-bin/webscr?cmd=_button-management" rel="external">PayPal button</a>.
      </p>
      <p>
        Once you've created the button, add the '<em>hosted_button_id</em>' to the respective external payment ID field below.
      </p>
    </div>

    <div class="row">
      <div class="col">
        <label for="purchasable-item-external-payment-sandbox-id">
          External Payment Sandbox ID:
        </label>
        <input
          type="text"
          name="purchasable_item[external_payment_sandbox_id]"
          id="purchasable-item-external-payment-sandbox-id"
          class="form-control"
          v-model="external_payment_sandbox_id"
        />
      </div>
      <div class="col">
        <label for="purchasable-item-external-payment-production-id">
          External Payment Production ID:
        </label>
        <input
          type="text"
          name="purchasable_item[external_payment_production_id]"
          id="purchasable-item-external-payment-production-id"
          class="form-control"
          v-model="external_payment_production_id"
        />
      </div>
    </div>
  </div>

  <div class="form-group">
    <div class="row">
      <div class="col">
        <starts-at-datetime
          labelText="Available At:"
          name="purchasable_item[starts_at]"
          :value="starts_at"
        >
        </starts-at-datetime>
      </div>
      <div class="col">
        <ends-at-datetime
          labelText="Available until:"
          name="purchasable_item[ends_at]"
          :value="ends_at"
        ></ends-at-datetime>
      </div>
    </div>
  </div>

  <div class="form-group alert alert-warning text-center">
    'Available at/until' assign the public purchase windows.
    Only assign an 'Available until' date if you want the item to be discontinued after that specific date.
  </div>

  <div class="form-group">
    <div class="row">
      <div class="col">
        <label for="purchasable-item-visible">
          <input name="purchasable_item[visible]" type="hidden" value="false" />
          <input
            type="checkbox"
            name="purchasable_item[visible]"
            id="purchasable-item-visible"
            v-model="visible"
          />
          Visible?
        </label>
      </div>
      <div class="col">
        <visible-at-datetime
          labelText="Visible At:"
          name="purchasable_item[visible_at]"
          :value="visible_at"
        />
      </div>
    </div>
  </div>

  <div
    v-if="subscription"
  >
    <hr />

    <p
      class="alert alert-info text-center"
    >
      The following is for designating a specific window for subscriptions.
      For example, if you want to sell a 2020 subscription, set
      the start at date to 01/01/2020 and end at date to 12/31/2020.
    </p>

    <div class="form-group">
      <div class="row">
        <div class="col">
          <subscription-start-at-datetime
            labelText="Subscription Starts At:"
            name="purchasable_item[subscription_starts_at]"
            :value="subscription_starts_at"
          />
        </div>

        <div class="col">
          <subscription-end-at-datetime
            labelText="Subscription Ends At:"
            name="purchasable_item[subscription_ends_at]"
            :value="subscription_ends_at"
          />
        </div>
      </div>
    </div>
  </div>

  <hr />

  <tag-select
    type="purchasable_item"
    :selectedTags="tagSlugs"
  ></tag-select>

  <hr />

  <h4>Purchasable Item Attachments</h4>

  <div class="form-group">
    <ol v-if="relationships.purchasable_item_attachments && relationships.purchasable_item_attachments.length">
      <li v-for="attachment in purchasable_item_attachments">
        {{ attachment.attributes ? attachment.attributes.filename : '' }}
         - {{ Math.ceil(attachment.attributes.byte_size / 1024) }}kb

        <label>
          Delete?
        </label> <input type="checkbox" name="purchasable_item[delete_attachments][]" :value="attachment.id" />
      </li>
    </ol>

    <div id="purchasable-item-attachments">
      <label for="purchasable-item-attachment">
        Add an Attachment
          <button class="btn btn-default" v-on:click="newAttachment">
            <strong>+</strong>
          </button>
      </label>
      <div>
        <input
          type="file" name="purchasable_item[attachment_files][]"
          class="purchasable-item-attachment"
          style="display: none;"
        />

        <p
          class="alert alert-warning text-center"
          role="alert"
        >
          Please note: the item cannot be emailed if the total attachment filesize is greater than 10MB.
        </p>
      </div>
    </div>
  </div>

  <button
    type="submit"
    class="form-control btn btn-primary"
  >
    {{ actionLbl }} Purchasable Item
    <spinner size="small" v-if="loading"></spinner>
  </button>
</form>
</template>

<script>
import Spinner from 'vue-simple-spinner'

import Datetime from '@/components/Datetime'
import TagSelect from '@/components/TagSelect'

import TagMixin from '@/mixins/TagMixin'

import PurchasableItemStore from '@/stores/PurchasableItemStore'

export default {
  data () {
    return PurchasableItemStore.state
  },

  computed: {
    actionLbl () {
      return this.purchasableItemId ? 'Edit' : 'Create'
    },

    actionUrl () {
      return this.apiUrl(
        '/purchasable_items' + (
          this.purchasableItemId ? ('/' + this.purchasableItemId) : ''
        ), true
      )
    },

    methodType () {
      return this.purchasableItemId ? 'PUT' : 'POST'
    },

    formattedSubscriptionIntv: {
      get () {
        if (this.subscription_interval) {
          const intv = this.subscription_interval.split('.')
          return intv[0]
        }
      },

      set (intv) {
        this.subscription_interval = intv + '.week'
      }
    },

    formattedEndsAt () {
      return this.$root.dateTimeLocalFmt(this.ends_at)
    },

    formattedStartsAt () {
      return this.$root.dateTimeLocalFmt(this.starts_at)
    },

    formattedVisibleAt () {
      return this.$root.dateTimeLocalFmt(this.visible_at)
    }
  },

  mixins: [
    TagMixin
  ],

  props: [
    'purchasableItemId'
  ],

  components: {
    'starts-at-datetime': Datetime,
    'ends-at-datetime': Datetime,
    'spinner': Spinner,
    'subscription-end-at-datetime': Datetime,
    'subscription-start-at-datetime': Datetime,
    'tag-select': TagSelect,
    'visible-at-datetime': Datetime
  },

  methods: {
    submit (ev) {
      // Ignore unverified fixed subscription submission requests.
      if (!this.verifyFixedSubscription(ev)) { return }

      this.loading = true

      this.bindSubmit(ev).then((resp) => {
        this.loading = false
        const purchasableItemId = resp.body.data.id

        this.$router.push({
          name: 'purchasable_item',
          params: {
            purchasableItemId: purchasableItemId
          }
        })
      }, (resp) => {
        this.loading = false
        this.processErrors(resp)
      })
    },

    verifyFixedSubscription (ev) {
      if (this.subscription_starts_at || this.subscription_ends_at) {
        const confirmStr = 'UNDERSTOOD'

        const userConfirmStr = prompt(
          'You have set a fixed subscription time for this item. ' +
          `Please note that this will update -all- purchased subscriptions with these start/end dates. This action cannot be undone!

Enter '${confirmStr}' below to confirm that you want to continue.`
        )

        if (!userConfirmStr || !userConfirmStr.length) {
          return
        }

        if (confirmStr !== userConfirmStr) {
          alert(`You didn't enter '${confirmStr}'. Please try again, or select cancel.`)
          this.submit(ev)
          return
        }
      }

      return true
    },

    newAttachment (e) {
      e.preventDefault()

      let attach = document.querySelectorAll('.purchasable-item-attachment')[0].cloneNode()
      attach.style.display = 'inline-block'
      document.querySelector('#purchasable-item-attachments').appendChild(attach)
    },

    processErrors (resp) {
      this.$parent.processErrors(resp)
    }
  },

  watch: {
    '$route' (to, from) {
      this.get()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/PurchasableItemForm.vue